import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, MoreHorizontal, Pencil, Trash } from "lucide-react";
import { Separator } from "./ui/separator";
import CountriesSelect from "./ui/countries-dropdowm";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { promptsAPI } from "@/services/api";

export function ClusterAction({
  cluster,
  prompts,
  setSelectedCluster,
}: {
  setSelectedCluster: (clId: string) => void;
  cluster: any;
  prompts: any;
}) {
  const { t } = useLocale();
  const [openDialog, setOpenDialog] = useState<"edit" | "delete" | null>(null);
  const [country, setCountry] = useState(cluster?.country || "US");
  const [clusterName, setClusterName] = useState(cluster?.title || "");
  const [clusterNameError, setClusterNameError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const updateClusterMutation = useMutation({
    mutationFn: async (payload: { title: string; country: string }) => {
      const res = await promptsAPI.updateCluster(cluster.id, payload);
      return res.cluster;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
    },
  });
  const deleteClusterMutation = useMutation({
    mutationFn: async () => {
      const res = await promptsAPI.deleteCluster(cluster.id);
      return res.cluster;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
    },
  });

  const onClusterEdit = () => {
    if (!clusterName.trim().length) {
      setClusterNameError("Cluster name cannot be empty");
      return;
    }

    updateClusterMutation.mutate(
      { title: clusterName.trim(), country },
      {
        onSuccess: () => {
          toast.success(t("legacy.the.cluster.name.was.updated.13yp1a9"), {
            icon: (
              <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                <Check className="w-4 h-4 text-black" />
              </div>
            ),
            position: "bottom-right",
          });
          setOpenDialog(null);
        },
        onError: () => {
          toast.error(t("legacy.failed.to.update.cluster.mbpejq"), {
            position: "bottom-right",
          });
        },
      },
    );
  };

  const onClusterDelete = () => {
    deleteClusterMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("legacy.cluster.deleted.qsez2q"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <Check className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
        setOpenDialog(null);
        setSelectedCluster("all-clusters");
      },
      onError: () => {
        toast.error(t("legacy.failed.to.delete.cluster.oep2oe"), {
          position: "bottom-right",
        });
      },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="p-2 bg-white cursor-pointer "
          >
            <div className="p-1 bg-white rounded-md cursor-pointer">
              <MoreHorizontal className="h-4 w-4" />
            </div>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setOpenDialog("edit")}
          >
            <Pencil className="text-[#0A0A0A]"></Pencil>
            <LocalizedText id="legacy.edit.yjfqt5" />
          </DropdownMenuItem>
          <Separator className="my-1 h-px w-full bg-border" />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setOpenDialog("delete")}
          >
            <Trash className="text-red-600"></Trash>
            <span className="text-red-600">
              <LocalizedText id="legacy.delete.16wmut8" />
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={openDialog !== null}
        onOpenChange={() => setOpenDialog(null)}
      >
        <DialogContent>
          {openDialog === "edit" && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0A0A0A] text-lg">
                  <LocalizedText id="legacy.edit.cluster.1frzrv7" />
                </DialogTitle>
                <DialogDescription className="text-sm text-[#737373]">
                  <LocalizedText id="legacy.edit.a.cluster.to.group.related.prompts.gpvn1q" />
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <div>
                  <Label
                    className="text-sm font-medium mb-1"
                    htmlFor="clusterName"
                  >
                    <LocalizedText id="legacy.name.yjpg1u" />
                  </Label>
                  <input
                    value={clusterName}
                    onChange={(e) => {
                      setClusterName(e.target.value);
                    }}
                    id="clusterName"
                    type="text"
                    autoComplete="off"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
                  />
                  {clusterNameError && (
                    <p className="text-red-500 text-sm mt-2">
                      {clusterNameError}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    className="text-sm font-medium mb-1"
                    htmlFor="clisterCountry"
                  >
                    <LocalizedText id="legacy.country.1oy8cul" />
                  </Label>
                  <CountriesSelect
                    isIcon={false}
                    id="clisterCountry"
                    setCountry={setCountry}
                    promptCountry={country}
                    isFull={true}
                  ></CountriesSelect>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => setOpenDialog(null)}
                  >
                    <LocalizedText id="legacy.cancel.18x3nqb" />
                  </Button>
                  <Button
                    onClick={onClusterEdit}
                    className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                  >
                    <LocalizedText id="legacy.save.yjzjb8" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {openDialog === "delete" && (
            <>
              <DialogHeader className="gap-4">
                <DialogTitle className="text-center">
                  <LocalizedText id="legacy.delete.this.cluster.1nem5jj" />
                </DialogTitle>
                <DialogDescription className="text-center">
                  {prompts.length ? (
                    <div>
                      <LocalizedRichText
                        id="clusterActions.delete.activePrompts"
                        values={{
                          count: (
                            <span className="font-semibold">
                              {prompts.length}
                            </span>
                          ),
                        }}
                      />
                    </div>
                  ) : (
                    <div>
                      <LocalizedText id="legacy.this.cluster.will.be.permanently.deleted.1umn48y" />
                    </div>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setOpenDialog(null)}
                >
                  <LocalizedText id="legacy.cancel.18x3nqb" />
                </Button>
                <Button
                  className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                  onClick={onClusterDelete}
                >
                  {prompts.length
                    ? "Deactivate prompts & Delete cluster"
                    : "Delete cluster"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
